/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EEForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import appTheme from "../../../../assets/AppTheme/appTheme";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import { useDispatch } from "react-redux";
import moment from "moment";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import actionType from "../../../../redux/actionTypes";
import typeApprovalAPI from "../../../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../../../redux/actions/ruleEngineAction";
import customConsole from "../../../../config/customConsole";
import { Link } from "react-router-dom";
import SelectTextFieldN from "../../../text-fields/SelectTextFieldN";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import SelectVehicleComponentForVehicleSystem from "../../../../pages/labcar/SelectVehicleComponentForVehicleSystem";

function EEForm({
  title,
  getZoneEECountList,
  openEEComponentReplaceDialog,
  setOpenEEComponentReplaceDialog,
  handleEEComponentReplace,
  selectedEEComponentReplaceDetails,
  setSelectedEEComponentReplaceDetails,
  vsOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedZoneDetails,
  selectedEEComponentDetails,
  getEEComponentsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  supplierOptionsList,
  disableFields,
}) {
  const [assemblyNo, setAssemblyNo] = useState("");
  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierNameError, setSupplierNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);

  const [selectedASIL, setSelectedASIL] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath.length) return;

    // copy up to index (inclusive) and set the chosen answer
    const newPath = rulePath
      .slice(0, idx + 1)
      .map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      // push the next node and reset its answer
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else {
      // If we have a category, this is a terminal node
      if (currentEntry?.node?.data?.category) {
        // Check if homologation is required for this category
        if (currentEntry?.node?.data?.requires_homologation) {
          // If homologation is required, call updateChangesAndClose
          updateChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        } else {
          // If homologation is not required, just close the dialog
          updateChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        }
      } else {
        // Otherwise just update the current answer and keep dialog open
        setRulePath(newPath);
      }
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (
      openEEComponentReplaceDialog == true &&
      selectedEEComponentReplaceDetails
    ) {
      setAssemblyNo(selectedEEComponentReplaceDetails?.assembly_number || "");
      setSupplierName(selectedEEComponentReplaceDetails?.supplier_name || "");
      setVersion(selectedEEComponentReplaceDetails?.version || "");
      setDescription(selectedEEComponentReplaceDetails?.description || "");
      setSummary(selectedEEComponentReplaceDetails?.summary || "");
      setSelectedVSIds(
        (selectedEEComponentReplaceDetails?.vehicle_systems || [])
          ?.map((vsId) => Number(vsId))
          ?.filter((vsId) => !isNaN(vsId)),
      );
      setSelectedASIL(
        selectedEEComponentReplaceDetails?.other_fields?.asil_class || null,
      );
      setDiscp(selectedEEComponentReplaceDetails?.reason || "");
      setSelectedVariantsMany(
        variantOptionList
          ?.filter((variant) =>
            // variant?.value != selectedSingleVariantDetails?.variant_id &&
            selectedEEComponentReplaceDetails?.variant_ids
              ?.map((variant) => variant)
              ?.includes(variant?.value),
          )
          ?.map((data) => String(data?.value)) || [],
      );
    }
  }, [openEEComponentReplaceDialog, selectedEEComponentReplaceDetails]);

  /* -------------- Replace EE Component -------------- */

  const clearReplaceEEComponentFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName("");
    setSupplierNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSelectedVSIds([]);
    setSelectedASIL(null);
    setDiscp("");
    setDiscpError("");
    setSelectedVariantsMany([]);
  };

  const isReplaceEEComponentValid = () => {
    return (
      !assemblyNoError &&
      !supplierNameError &&
      !versionError &&
      !descriptionError &&
      selectedVSIds?.length > 0 &&
      selectedASIL?.length > 0 &&
      discp?.trim().length > 0 &&
      (assemblyNo?.trim() !==
        selectedEEComponentReplaceDetails?.assembly_number ||
        supplierName?.trim() !==
        selectedEEComponentReplaceDetails?.supplier_name ||
        version?.trim() !== selectedEEComponentReplaceDetails?.version ||
        description?.trim() !==
        selectedEEComponentReplaceDetails?.description ||
        summary?.trim() !== selectedEEComponentReplaceDetails?.summary ||
        selectedVSIds?.length !==
        selectedEEComponentReplaceDetails?.vehicle_systems?.length ||
        selectedASIL !==
        selectedEEComponentReplaceDetails?.other_fields?.asil_class)
    );
  };

  const isUpdateDraftEEComponentValid = () => {
    return (
      !assemblyNoError &&
      !supplierNameError &&
      !versionError &&
      !descriptionError &&
      selectedVSIds?.length > 0 &&
      selectedASIL?.length > 0
    );
  };
  const isUpdateAgainDraftEEComponentValid = () => {
    return !assemblyNoError;
  };

  const handleCloseEEComponentReplace = () => {
    setOpenEEComponentReplaceDialog(false);

    clearReplaceEEComponentFields();

    setSelectedEEComponentReplaceDetails(null);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
  };

  const handleUpdateClick = () => {
    setShowConfirmation(true); // Show confirmation dialog when update is clicked
  };

  const handleUpdateDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      ...(selectedVariantsMany?.length > 0
        ? { variant_ids: selectedVariantsMany }
        : []),
      save_as_draft: false,
      assembly_number: assemblyNo,
      supplier_name: supplierName,

      version: version,
      ...(summary ? { summary: summary } : null),
      description: description,
      vehicle_systems: selectedVSIds?.map(Number),

      other_fields: {
        asil_class: selectedASIL,
      },

      current_time: moment().format(),
    };
    dispatch(
      HandleApiActions({
        api_name: "/PCM/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
    });
  };

  const handleUpdateAgainDraftClickEE = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      variant_ids: selectedVariantsMany,
      save_as_draft: true,
      assembly_number: assemblyNo,
      ...(supplierName ? { supplier_name: supplierName } : null),

      ...(version ? { version: version } : null),
      ...(summary ? { summary: summary } : null),
      ...(description ? { description: description } : null),
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(selectedASIL?.length > 0
        ? {
          other_fields: {
            asil_class: selectedASIL,
          },
        }
        : null),
      current_time: moment().format(),
    };

    dispatch(
      HandleApiActions({
        api_name: "/PCM/ee_components/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_EE_COMPONENT_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentReplace();
    });
  };

  const confirmUpdateAndRedirect = async ({ willImptIntrOnly = false }) => {
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      const reqObj = {
        current_time: new Date().toISOString(),
        variant_ids: selectedVariantsMany,
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo,
        ...(supplierName && { supplier_name: supplierName }),
        version: version,
        ...(summary && { summary }),
        description: description,
        vehicle_systems:
          selectedVSIds?.map((e) => {
            return e.value;
          }) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!response.error) {
          handleCloseEEComponentReplace();
          willImptIntrOnly
            ? setActionType2DialogOpen(true)
            : setActionType1DialogOpen(true);
          if (willImptIntrOnly) {
            const resp = await dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(
                    selectedEEComponentReplaceDetails?.project_id,
                  ),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            );
          }
          getEEComponentsList();
          getSWModulesList();
        }
      } catch (error) {
      }
    }
  };

  const handleCloseEEComponentReplaceDialog = () => {
    setOpenEEComponentReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      tickets_type: 2,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              .map((ticket) => ticket?.ls_details?.standard_number)
              .filter((std) => Boolean(std)) // remove empty/undefined
            : [],
        ),
      ];

      if (standardsWithRuleSet.length > 0) {
        // Second API call
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet.data.length > 0
            : Object.keys(standardWithRuleSet.data).length > 0)
        ) {
          handleCloseEEComponentReplaceDialog();
          setShowRuleSetListDialog(true);
          setRuleSetList(standardWithRuleSet.data);
        } else {
          updateChangesWithoutStandardAndClose();
        }
        return standardWithRuleSet.data || [];
      } else {
        updateChangesAndClose();
        return [];
      }
    } catch (error) {
      return false;
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseEEComponentReplace();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_rule_set_tree,
          params: {
            rule_set_id: ruleSetIdToUse,
          },
          show_toast: false,
        }),
      );

      if (response?.data) {
        // Store the full tree and open the question dialog
        setRuleSetTree(response.data);
        // Normalize root node into { type, data }
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setShowRuleSetQuestionDialog(true);
          setRulePath([{ node: root, answer: null }]);
        }
      }
    } catch (error) {
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedVariantsMany,
      project_id: Number(selectedEEComponentReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
      version: version,
      will_it_impact: false,
      requires_homologation: true,
      reason: discp,
    };

    try {
      // Await the dispatch to ensure you're getting the response
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_ee_component,
          params: reqObj,
          show_toast: true,
        }),
      );
      getEEComponentsList();
      getSWModulesList();
      if (!response.error) {
        handleCloseEEComponentReplace();
      }
      if (response?.data?.snow_resp) {
        setTicketsDialogOpen(true);
      }
    } catch (error) {
    }
  };

  const updateChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      const reqObj = {
        variant_ids: selectedVariantsMany,
        current_time: new Date().toISOString(),
        project_id: Number(selectedEEComponentReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo,
        supplier_name: supplierName,
        version: version,
        ...(summary && { summary }),
        description: description,
        vehicle_systems: selectedVSIds?.map(Number) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_ee_component,
            params: reqObj,
            show_toast: true,
          }),
        );
        getEEComponentsList();
        getSWModulesList();
        if (!response.error) {
          handleCloseEEComponentReplace();
        }
        if (response?.data?.snow_resp) {
          setTicketsDialogOpen(true);
        }
      } catch (error) {
      }
    }
  };

  const handleReplaceEEComponent = ({ }) => {
    if (
      selectedEEComponentReplaceDetails &&
      selectedEEComponentReplaceDetails?.ee_id
    ) {
      handleEEComponentReplace({
        ee_id: Number(selectedEEComponentReplaceDetails?.ee_id),
        assembly_number: assemblyNo,
        supplier_name: supplierName,
        version: version,
        summary: summary,
        description: description,
        vehicle_systems: selectedVSIds?.map(Number) || [],
        other_fields: {
          asil_class: selectedASIL,
        },
      });

      handleCloseEEComponentReplace();
    }
  };

  useEffect(() => {
    customConsole({ selectedVSIds, vsOptionsList });
  }, [selectedVSIds, vsOptionsList]);

  const handleActionType1DialogOpen = (ecu) => {
    setActionType1DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Closes the action type 2 dialog.
   */
  /*******  8bbe441d-5db6-4801-843b-8073c92b7f62  *******/ const handleActionType2DialogClose =
    () => {
      setActionType2DialogOpen(false);
    };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  return (
    <Box>
      <Stack
        spacing={2}
        mb={2}
        justifyContent={"center"}
        sx={{ width: "100%" }}
      >
        <CustomFormTextFieldV2
          size={"small"}
          label={"Assembly No"}
          placeholder={"Assembly No*"}
          type={"text"}
          fldType={"textR"}
          value={assemblyNo}
          setValue={setAssemblyNo}
          valueError={assemblyNoError}
          setValueError={setAssemblyNoError}
          disabled={disableFields.includes("assemblyNo")}

        />

        <CustomSelectV2
          size={"small"}
          required={false}
          label={"Select Supplier Name"}
          optionsList={supplierOptionsList}
          selectedValue={supplierName}
          handleOnChange={(e) => setSupplierName(e.target.value)}
          disabled={disableFields.includes("supplierName")}
        />
        <CustomFormTextFieldV2
          size={"small"}
          label={"Version*"}
          placeholder={"Version"}
          type={"text"}
          fldType={"textR"}
          value={version}
          setValue={setVersion}
          valueError={versionError}
          setValueError={setVersionError}
          disabled={disableFields.includes("version")}
        />
        <CustomFormTextFieldV2
          size={"small"}
          label={"Description"}
          placeholder={"Description*"}
          type={"text"}
          fldType={"text"}
          value={description}
          setValue={setDescription}
          valueError={descriptionError}
          setValueError={setDescriptionError}
          disabled={disableFields.includes("description")}
        />
        <CustomFormTextFieldV2
          size={"small"}
          label={"Summary"}
          placeholder={"Summary"}
          type={"text"}
          fldType={"text"}
          value={summary}
          setValue={setSummary}
          valueError={summaryError}
          setValueError={setSummaryError}
          disabled={disableFields.includes("summary")}
        />

        <SelectVehicleComponentForVehicleSystem
          open={openEEComponentReplaceDialog}
          projectId={selectedProjectDetails?.project_id}
          variantId={selectedSingleVariantDetails?.variant_id}
          isZonal={selectedProjectDetails?.is_zonal}
          zonePosition={selectedZoneDetails?.position}
          setVcIdError={() => { }}
          vcId={
            selectedEEComponentReplaceDetails?.vehicle_component_details?.[0]
              ?.vc_id
          }
          vsId={
            selectedEEComponentReplaceDetails?.vehicle_component_details?.[0]
              ?.vs_id
          }
        />


        <CustomSelectV2
          required={false}
          size={"small"}
          label={"Select ASIL Class"}
          optionsList={["A", "B", "C", "D"]?.map((el) => ({
            value: el,
            label: el,
          }))}
          selectedValue={selectedASIL}
          handleOnChange={(e) => setSelectedASIL(e.target.value)}
          disabled={disableFields.includes("asilClass")}
        />

        <MultiselectConnectorV2
          multiple
          selectedValues={selectedVariantsMany}
          setSelectedValues={setSelectedVariantsMany}
          disabled={disableFields.includes("variants")}
          label={"Select Variants"}
          optionsList={(variantOptionList || [])?.filter((variants) => {
            const currentId = selectedSingleVariantDetails?.variant_id;
            const alreadyAdded = (
              selectedEEComponentReplaceDetails?.variant_ids || []
            )
              ?.map((v) => String(v))
              ?.includes(String(variants?.value));
            return (
              alreadyAdded || String(variants?.value) === String(currentId)
            );
          })}
          immutableValues={(() => {
            const opt = (variantOptionList || [])?.find(
              (v) =>
                String(v?.value) ===
                String(selectedSingleVariantDetails?.variant_id),
            );
            return opt && opt.value != null ? [opt.value] : [];
          })()}
          // optionsList={variantOptionList}
          placeholder={false}
        />


        {showConfirmation && (
          <Stack spacing={2}>
            <Typography variant="body1">
              Will it impact the existing dependencies?
            </Typography>
            <Stack direction="row" spacing={2} justifyContent={"center"}>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={confirmUpdateAndRedirect}
              >
                Yes
              </Button>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={() => {
                  setShowConfirmation(false);
                  setShowIntersectionImpactPrompt(true);
                }}
              >
                No
              </Button>
            </Stack>
          </Stack>
        )}

        {showIntersectionImpactPrompt && (
          <Stack spacing={2}>
            <Typography variant="body1">
              Will it impact the existing intersection?
            </Typography>
            <Stack direction="row" spacing={2} justifyContent={"center"}>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={() =>
                  confirmUpdateAndRedirect({ willImptIntrOnly: true })
                }
              >
                Yes
              </Button>
              <Button
                sx={{ ...appTheme.commonBtnStyle }}
                onClick={checkForStandardWithRuleSet}
              >
                No
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>


      {/* Action Pending Dialog */}
      <Box>
        <Dialog
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          {/* Close Button */}
          <IconButton
            onClick={handleActionType1DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                ECU dependency linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/labcar?ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`}
                >
                  <Button
                    sx={{ ...appTheme.commonBtnStyle }}
                    onClick={() => setIsLinkSoftwareModulesScreen(true)}
                  >
                    Link Now
                  </Button>
                </Link>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleActionType1DialogClose}
                >
                  Link Later
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Action Pending Dialog */}
        <Dialog
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Action required</DialogTitle>
          {/* Close Button */}
          <IconButton
            onClick={handleActionType2DialogClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                ECU intersection linking is pending!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${selectedProjectDetails?.project_id}&variant_id=${selectedSingleVariantDetails?.variant_id}&ee_id=${selectedEEComponentDetails?.ee_id}&selectedZone=${selectedZone?.zone_position_name}`}
                >
                  <Button
                    sx={{ ...appTheme.commonBtnStyle }}
                    onClick={handleActionType2DialogClose}
                  >
                    Link Now
                  </Button>
                </Link>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleActionType2DialogClose}
                >
                  Link Later
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Handle Ticket Page Dialog */}
        <Dialog
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
        >
          <DialogTitle align="left">Tickets Recreated</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body1" align="center">
                Successfully created the tickets for the intersections!
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Link
                  to={`/dashboard/type-approval?project_id=${selectedProjectDetails?.project_id
                    }&setTabValue=${2}&variant_id=${selectedSingleVariantDetails?.variant_id
                    }`}
                >
                  <Button sx={{ ...appTheme.commonBtnStyle }}>View</Button>
                </Link>

                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleTicketsDialogClose}
                >
                  Close
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
        {/* Rule set list dialog */}
        <Dialog
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
        >
          <DialogTitle align="left">Select Rule Set</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 320 }}>
              <SelectTextFieldN
                size={"small"}
                label={"Select Rule Set"}
                variant={"standard"}
                optionsList={
                  ruleSetList?.map((rs) => ({
                    value: rs?.rule_set_id || rs?.id,
                    label: rs?.name,
                  })) || []
                }
                selectedValue={selectedRuleSet}
                handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
                sltFldStyle={{ width: "100%", marginTop: 6 }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={handleRuleSetListDialogClose}
                >
                  Cancel
                </Button>
                <Button
                  sx={{ ...appTheme.commonBtnStyle }}
                  onClick={async () => {
                    // fetch and open the question flow for the selected ruleset
                    await viewRuleSetQuestions(selectedRuleSet);
                    // keep the list dialog closed
                    setShowRuleSetListDialog(false);
                  }}
                  disabled={!selectedRuleSet}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Rule set question dialog - progressive yes/no navigation */}
        <Dialog
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
        >
          <DialogTitle>Rule Set: {ruleSetTree?.name || ""}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 360 }}>
              {/* Full path view - show all questions answered so far and allow editing */}
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  return (
                    <Box
                      key={idx}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {!isCategory ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              size="small"
                              variant={
                                entry.answer === "yes"
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() => handleAnswerAt(idx, "yes")}
                              sx={{
                                ...appTheme.commonBtnStyle,
                                mr: 1,
                                ...(entry.answer === "no" && {
                                  color: "#888",
                                  borderColor: "#d0d0d0",
                                  backgroundColor: "#f5f5f5",
                                  "&:hover": {
                                    backgroundColor: "#eeeeee",
                                    borderColor: "#c0c0c0",
                                  },
                                }),
                              }}
                            >
                              Yes
                            </Button>
                            <Button
                              size="small"
                              variant={
                                entry.answer === "no" ? "contained" : "outlined"
                              }
                              onClick={() => handleAnswerAt(idx, "no")}
                              sx={{
                                ...appTheme.commonBtnStyle,
                                ...(entry.answer === "yes" && {
                                  color: "#888",
                                  borderColor: "#d0d0d0",
                                  backgroundColor: "#f5f5f5",
                                  "&:hover": {
                                    backgroundColor: "#eeeeee",
                                    borderColor: "#c0c0c0",
                                  },
                                }),
                              }}
                            >
                              No
                            </Button>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>

                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {/* If last node is category show Finish, otherwise allow answering last question if unanswered */}
                {rulePath?.length > 0 &&
                  rulePath[rulePath.length - 1]?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <Button
                      sx={{ ...appTheme.commonBtnStyle }}
                      onClick={() => {
                        // Check if the last node requires homologation
                        const lastNode = rulePath[rulePath.length - 1]?.node;

                        if (lastNode?.data?.requires_homologation) {
                          // If homologation is required, just call the updateChangesAndClose function and close the dialog
                          updateChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseEEComponentReplace();
                        } else {
                          // If homologation is not required, just close the dialog
                          updateChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseEEComponentReplace();
                        }
                      }}
                    >
                      Finish
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}

EEForm.propTypes = {
  // Title
  title: PropTypes.string,

  // Dialog control
  openEEComponentReplaceDialog: PropTypes.bool,
  setOpenEEComponentReplaceDialog: PropTypes.func,

  // API / Data fetch
  getZoneEECountList: PropTypes.func,
  getEEComponentsList: PropTypes.func,
  getSWModulesList: PropTypes.func,

  // Handlers
  handleEEComponentReplace: PropTypes.func,
  setSelectedEEComponentReplaceDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,

  // Lists
  vsOptionsList: PropTypes.array,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,

  // Selected objects
  selectedEEComponentReplaceDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedZoneDetails: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,

  // UI control
  disableFields: PropTypes.array,
};

export default EEForm;

const styles = {
  closeButton: {
    color: "black",
    position: "absolute",
    top: { xs: "2px", sm: "4px" },
    right: { xs: "4px", sm: "8px" },
  },
  eeCompMainContainer: {
    flex: 1,
    minWidth: 0,
    minHeight: { xs: "350px", sm: "400px", md: "500px" },
    maxHeight: { xs: "500px", sm: "600px", md: "700px" },
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "stretch",
    boxSizing: "border-box",
  },
  eeCompCard: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  eeCompCardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
    // "& .MuiTypography-root": {
    //   fontSize: { xs: "0.6rem", sm: "0.9rem" },
    // },
  },
  eeCompCardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid lightgrey",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  eeCompsList: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  eeCompsListItemContainer: {
    // flexGrow: 1,
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    // display: "flex",
    // flexDirection: "column",
    gap: 0.5,
    // fontSize: { xs: "0.75rem", sm: "0.85rem" },
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  txtStyle: {
    width: "270px",
  },
  txtStyleR: {
    width: "270px",
  },
  edRpIconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 0.5, sm: 1 },
  },
  customTextFieldN: {
    width: "270px",
  },
  selectTxtStyle: {
    width: "270px",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
};
